import type { Caption } from '@remotion/captions';

import {
  isCaptionAsset,
  type CaptionAsset,
  type EditorStarterAsset,
} from '../../assets/assets';
import type { EditorStarterItem } from '../item-type';
import type { TrackType } from '../../state/types';
import { isCaptionsItem, type CaptionsItem } from './captions-item-type';
import { CAPTION_GAP_SPLIT_THRESHOLD_MS } from './split-captions-by-gap';

type CaptionWithPresentationMetadata = Caption & {
  speaker?: string[];
  captionGroupId?: string;
};

export type TimelineCaptionData = {
  captions: Caption[];
  assetEndMs: number;
};

export type CaptionPresentationGroup = {
  items: CaptionsItem[];
};

export type CaptionPresentationRenderData = {
  captions: Caption[];
  captionStartInSeconds: number;
  displayEndMs: number;
  from: number;
  durationInFrames: number;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const frameToMs = (frame: number, fps: number) =>
  Math.round((frame / fps) * 1000);

const cloneCaptions = (captions: Caption[]): Caption[] =>
  captions.map((caption) => ({ ...caption }));

const sortCaptionsByStart = (captions: Caption[]): Caption[] =>
  [...captions].sort((left, right) => left.startMs - right.startMs);

export const getPrimaryCaptionSpeaker = (
  caption: Caption,
): string | undefined => {
  const speaker = (caption as CaptionWithPresentationMetadata).speaker;
  return Array.isArray(speaker) && speaker.length > 0 ? speaker[0] : undefined;
};

const getCaptionGroupId = (caption: Caption): string | undefined => {
  const groupId = (caption as CaptionWithPresentationMetadata).captionGroupId;
  return typeof groupId === 'string' && groupId.length > 0
    ? groupId
    : undefined;
};

const withCaptionGroupId = (
  caption: Caption,
  groupId: string | undefined,
): Caption => {
  const nextCaption: CaptionWithPresentationMetadata = { ...caption };

  if (groupId === undefined) {
    delete nextCaption.captionGroupId;
  } else {
    nextCaption.captionGroupId = groupId;
  }

  return nextCaption;
};

/**
 * 두 자막 배열의 맞닿은 caption group만 표시상 연결한다.
 * 원본 배열과 원본 caption 객체는 변경하지 않는다.
 */
export const mergeBoundaryCaptionGroups = (
  previousCaptions: Caption[],
  currentCaptions: Caption[],
): Caption[] => {
  if (previousCaptions.length === 0 || currentCaptions.length === 0) {
    return sortCaptionsByStart([...previousCaptions, ...currentCaptions]);
  }

  const previousBoundaryGroupId = getCaptionGroupId(
    previousCaptions[previousCaptions.length - 1]!,
  );
  const currentBoundaryGroupId = getCaptionGroupId(currentCaptions[0]!);
  let isInBoundaryGroup = true;

  const normalizedCurrentCaptions = currentCaptions.map((caption) => {
    if (!isInBoundaryGroup) {
      return caption;
    }

    if (getCaptionGroupId(caption) !== currentBoundaryGroupId) {
      isInBoundaryGroup = false;
      return caption;
    }

    return withCaptionGroupId(caption, previousBoundaryGroupId);
  });

  return sortCaptionsByStart([
    ...previousCaptions,
    ...normalizedCurrentCaptions,
  ]);
};

export const getConsistentCaptionSceneId = (
  item: CaptionsItem,
  asset: CaptionAsset,
): number | null | undefined => {
  if (!isFiniteNumber(asset.sceneId) || asset.sceneId < 0) {
    return null;
  }

  if (item.renderMeta != null && item.renderMeta.sceneId !== asset.sceneId) {
    return undefined;
  }

  return asset.sceneId;
};

/**
 * asset의 원본 절대 타이밍을 현재 timeline 위치로 투영한다.
 */
export const normalizeCaptionItemToTimeline = ({
  item,
  asset,
  fps,
}: {
  item: CaptionsItem;
  asset: CaptionAsset;
  fps: number;
}): TimelineCaptionData => {
  const safeFps = fps > 0 ? fps : 30;
  const itemStartMs = frameToMs(item.from, safeFps);
  const itemEndMs = frameToMs(item.from + item.durationInFrames, safeFps);
  const captionOriginMs = isFiniteNumber(item.captionStartInSeconds)
    ? item.captionStartInSeconds * 1000
    : isFiniteNumber(asset.videoStart)
      ? asset.videoStart
      : itemStartMs;
  const offsetMs = itemStartMs - captionOriginMs;
  const captions = sortCaptionsByStart(
    cloneCaptions(asset.captions).map((caption) => ({
      ...caption,
      startMs: Math.round(caption.startMs + offsetMs),
      endMs: Math.round(caption.endMs + offsetMs),
      timestampMs:
        caption.timestampMs == null
          ? null
          : Math.round(caption.timestampMs + offsetMs),
    })),
  );
  const assetEndMs = isFiniteNumber(asset.videoEnd)
    ? asset.videoEnd + offsetMs
    : itemEndMs;

  return {
    captions,
    assetEndMs: Math.max(itemEndMs, assetEndMs),
  };
};

const isCompatiblePresentationBoundary = ({
  previousItem,
  currentItem,
  previousAsset,
  currentAsset,
  fps,
}: {
  previousItem: CaptionsItem;
  currentItem: CaptionsItem;
  previousAsset: CaptionAsset;
  currentAsset: CaptionAsset;
  fps: number;
}): boolean => {
  const previousSceneId = getConsistentCaptionSceneId(
    previousItem,
    previousAsset,
  );
  const currentSceneId = getConsistentCaptionSceneId(currentItem, currentAsset);
  if (
    previousSceneId == null ||
    currentSceneId == null ||
    previousSceneId === currentSceneId
  ) {
    return false;
  }

  const previousTimelineData = normalizeCaptionItemToTimeline({
    item: previousItem,
    asset: previousAsset,
    fps,
  });
  const currentTimelineData = normalizeCaptionItemToTimeline({
    item: currentItem,
    asset: currentAsset,
    fps,
  });
  const previousBoundaryCaption = previousTimelineData.captions.at(-1);
  const currentBoundaryCaption = currentTimelineData.captions[0];
  if (!previousBoundaryCaption || !currentBoundaryCaption) {
    return false;
  }

  const previousSpeaker = getPrimaryCaptionSpeaker(previousBoundaryCaption);
  const currentSpeaker = getPrimaryCaptionSpeaker(currentBoundaryCaption);
  if (
    previousSpeaker !== undefined &&
    currentSpeaker !== undefined &&
    previousSpeaker !== currentSpeaker
  ) {
    return false;
  }

  const gapMs = Math.max(
    0,
    currentBoundaryCaption.startMs - previousBoundaryCaption.endMs,
  );
  return gapMs < CAPTION_GAP_SPLIT_THRESHOLD_MS;
};

const getActiveMainCaptionTrack = (
  tracks: TrackType[],
  itemId: string,
): TrackType | undefined =>
  tracks.find(
    (track) =>
      track.metadata?.isMainTrack &&
      track.metadata.type === 'caption' &&
      track.items.includes(itemId),
  );

const isActivePresentationBoundary = ({
  previousItemId,
  currentItemId,
  items,
  assets,
  fps,
}: {
  previousItemId: string;
  currentItemId: string;
  items: Record<string, EditorStarterItem>;
  assets: Record<string, EditorStarterAsset>;
  fps: number;
}): boolean => {
  const previousItem = items[previousItemId];
  const currentItem = items[currentItemId];
  if (!isCaptionsItem(previousItem) || !isCaptionsItem(currentItem)) {
    return false;
  }
  if (previousItem.captionPresentationNextItemId !== currentItem.id) {
    return false;
  }

  const previousAsset = assets[previousItem.assetId];
  const currentAsset = assets[currentItem.assetId];
  if (!isCaptionAsset(previousAsset) || !isCaptionAsset(currentAsset)) {
    return false;
  }

  return isCompatiblePresentationBoundary({
    previousItem,
    currentItem,
    previousAsset,
    currentAsset,
    fps,
  });
};

/**
 * 저장된 표시 링크가 현재 메인 자막 트랙에서도 바로 인접할 때만 그룹으로 인정한다.
 * 복제·삭제·재정렬로 낡은 링크가 되면 null로 안전하게 퇴행한다.
 */
export const resolveCaptionPresentationGroup = ({
  itemId,
  tracks,
  items,
  assets,
  fps,
}: {
  itemId: string;
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  assets: Record<string, EditorStarterAsset>;
  fps: number;
}): CaptionPresentationGroup | null => {
  const track = getActiveMainCaptionTrack(tracks, itemId);
  if (!track) return null;

  const itemIndex = track.items.indexOf(itemId);
  if (itemIndex < 0) return null;

  let firstIndex = itemIndex;
  while (firstIndex > 0) {
    const previousItemId = track.items[firstIndex - 1];
    const currentItemId = track.items[firstIndex];
    if (
      !previousItemId ||
      !currentItemId ||
      !isActivePresentationBoundary({
        previousItemId,
        currentItemId,
        items,
        assets,
        fps,
      })
    ) {
      break;
    }
    firstIndex -= 1;
  }

  let lastIndex = itemIndex;
  while (lastIndex < track.items.length - 1) {
    const previousItemId = track.items[lastIndex];
    const currentItemId = track.items[lastIndex + 1];
    if (
      !previousItemId ||
      !currentItemId ||
      !isActivePresentationBoundary({
        previousItemId,
        currentItemId,
        items,
        assets,
        fps,
      })
    ) {
      break;
    }
    lastIndex += 1;
  }

  if (firstIndex === lastIndex) return null;

  const groupItems = track.items
    .slice(firstIndex, lastIndex + 1)
    .map((id) => items[id])
    .filter(isCaptionsItem);
  if (groupItems.length !== lastIndex - firstIndex + 1) {
    return null;
  }

  return { items: groupItems };
};

export const buildCaptionPresentationRenderData = ({
  items,
  assets,
  fps,
}: {
  items: CaptionsItem[];
  assets: Record<string, EditorStarterAsset>;
  fps: number;
}): CaptionPresentationRenderData | null => {
  if (items.length < 2) return null;

  const safeFps = fps > 0 ? fps : 30;
  let captions: Caption[] = [];
  let displayEndMs = 0;

  for (const [index, item] of items.entries()) {
    const asset = assets[item.assetId];
    if (!isCaptionAsset(asset)) return null;

    const timelineData = normalizeCaptionItemToTimeline({
      item,
      asset,
      fps: safeFps,
    });
    captions =
      index === 0
        ? timelineData.captions
        : mergeBoundaryCaptionGroups(captions, timelineData.captions);
    displayEndMs = Math.max(displayEndMs, timelineData.assetEndMs);
  }

  if (captions.length === 0) return null;

  const from = Math.min(...items.map((item) => item.from));
  const itemEndFrame = Math.max(
    ...items.map((item) => item.from + item.durationInFrames),
  );
  const displayEndFrame = Math.ceil((displayEndMs / 1000) * safeFps);
  const endFrame = Math.max(from + 1, itemEndFrame, displayEndFrame);

  return {
    captions,
    captionStartInSeconds: frameToMs(from, safeFps) / 1000,
    displayEndMs,
    from,
    durationInFrames: endFrame - from,
  };
};
